import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { FormWrap } from '../FormContainer/FormContainer';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';
import { useApplicantData } from '../../hooks/useApplicantData';

import useStepPost from '../../hooks/useStepPost';
import { APPLICANT_TYPES } from '../../util/constants';
import useApplicationOwnerCountry from '../../hooks/useApplicationOwnerCountry';
import { isPositiveInteger } from '../../util/helpers/ssnHelper';
import usePersonSsnDispatch from '../../hooks/usePersonSsnDispatch';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import themeColors from '../../assets/styles/themeColors';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
} from '../../constants/sessionStorage';
import SaveAndCloseModal from '../Modals/SaveAndCloseModal/SaveAndCloseModal';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import CodeField from '../InputFields/CodeField';
import { getSelectedDaysToShow4DigitSSNForm } from '../../util/helpers/dateHelpers';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;

  button {
    position: absolute;
    top: 32px;
    right: 0;
    margin: 12px;
  }

  svg {
    width: 24px;
    height: 24px;
    color: ${themeColors.colorSvgSemidark};
  }

  input {
    padding-right: 42px;
  }
`;
const LastFourSsnForm = ({
  nextPath,
  applicantType,
  onModalClose,
  formId,
  setEventType,
  eventType,
}) => {
  const { t } = useTranslation();
  const ApplicantData = useApplicantData(applicantType);
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const [actualSsn, setActualSsn] = useState('');
  const { identityCountry } = useApplicationOwnerCountry(applicantType);
  const { personSsnDispatch } = usePersonSsnDispatch();
  const isSalesAssisted = retrieveFromSessionStorage(
    IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  );
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const selectedDaysToShow4DigitForm = getSelectedDaysToShow4DigitSSNForm();

  const defaultValues = useMemo(() => {
    const initialSsn = getApplicantData(ApplicantData, 'PersonSsn', 'Ssn');
    return {
      SecurityCode:
        initialSsn?.length > 4 ? initialSsn?.slice(-4) : initialSsn || '',
    };
  }, [ApplicantData]);

  const { handleSubmit, control, setError, setValue } = useForm({
    defaultValues,
  });

  useEffect(() => {
    const val = getApplicantData(ApplicantData, 'PersonSsn', 'Ssn');
    if (val !== null) {
      setActualSsn(val);
      let initialSsn = null;
      if (val?.length > 4) {
        initialSsn = val.slice(-4);
        setValue('SecurityCode', initialSsn, {
          shouldInvalidate: false,
          shouldDirty: false,
        });
        return;
      }

      setValue('SecurityCode', val, {
        shouldInvalidate: false,
        shouldDirty: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ApplicantData]);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    if (
      applicantType === APPLICANT_TYPES.MAIN &&
      ApplicantData?.PersonName &&
      ApplicantData?.PersonBirthday
    ) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Secure Social Security Number',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountPhone: ApplicantData?.Phone,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
        customParams: {
          timestamp: new Date().toISOString(),
        },
      });
      return;
    }
    if (ApplicantData?.PersonName && ApplicantData?.PersonBirthday) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Secure Social Security Number Second Owner',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
        accountPhone: ApplicantData?.Phone,
        customParams: {
          timestamp: new Date().toISOString(),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    const birthDate = ApplicantData?.PersonBirthday;
    if (applicantType === APPLICANT_TYPES.MAIN) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_COMPLETE,
        stepLabel: 'Secure Social Security Number',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountPhone: ApplicantData?.Phone,
        accountDateOfBirth: birthDate,
        customParams: {
          timestamp: new Date().toISOString(),
        },
      });
      return;
    }
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Secure Social Security Number Second Owner',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountPhone: ApplicantData?.Phone,
      accountDateOfBirth: birthDate,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const onSubmit = () => {
    if (!ApplicantData?.PersonUid) {
      return;
    }
    if (isSalesAssisted && actualSsn === '') {
      personSsnDispatch({
        personUid: ApplicantData?.PersonUid,
        data: {
          Ssn: actualSsn,
        },
        defaultValues,
        onSuccess:
          eventType === 'Form'
            ? onSuccess
            : () => {
                setHasError(false);
                setSaveAndCloseModalOpen(true);
              },
        onError:
          eventType === 'Form'
            ? () => {}
            : () => {
                setHasError(true);
                setSaveAndCloseModalOpen(true);
              },
      });
      return;
    }

    if (!actualSsn) {
      setError('SecurityCode', {
        message: t('fields.errors.validation.ssnRequired'),
      });
      return;
    }

    if (actualSsn.length && actualSsn.length < 4) {
      setError('SecurityCode', {
        message: t('fields.errors.validation.ssnNotValid'),
      });
      return;
    }

    if (!isPositiveInteger(actualSsn) && !selectedDaysToShow4DigitForm) {
      setError('SecurityCode', {
        message: t('fields.errors.validation.ssnDigits'),
      });
      return;
    }
    personSsnDispatch({
      personUid: ApplicantData?.PersonUid,
      data: {
        Ssn: actualSsn,
      },
      defaultValues,
      onSuccess:
        eventType === 'Form'
          ? onSuccess
          : () => {
              setHasError(false);
              setSaveAndCloseModalOpen(true);
            },
      onError:
        eventType === 'Form'
          ? () => {}
          : () => {
              setHasError(true);
              setSaveAndCloseModalOpen(true);
            },
    });
    handleGtmEvent();
  };

  const handleCodeChange = (value) => {
    if (value) {
      setActualSsn(value);
      setValue('SecurityCode', value, {
        sholdInvalidate: true,
        shouldDirty: true,
      });
    }
  };

  return (
    <FormWrap id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        render={({ field: { value }, formState: { errors } }) => (
          <Container>
            <CodeField
              numFields={4}
              value={value ? value.slice(-4) : ''}
              fixedSize
              fieldName="SecurityCode"
              errors={errors}
              onCodeChange={(value) => handleCodeChange(value)}
              hideExpireCodeNote
              mozillaStyle="textfield"
            />
          </Container>
        )}
        name="SecurityCode"
      />

      <OnBoardingCta
        type="submit"
        onClick={() => setEventType('Form')}
        disabled={identityCountry !== 'USA' && !isSalesAssisted}
        title={nextPath ? t('continue') : t('update')}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          hasError={hasError}
          onSuccess={onSuccess}
        />
      )}
    </FormWrap>
  );
};

LastFourSsnForm.propTypes = {
  nextPath: PropTypes.string,
  applicantType: PropTypes.string,
  onModalClose: PropTypes.func,
  formId: PropTypes.string,
  eventType: PropTypes.string,
  setEventType: PropTypes.func,
};

export default LastFourSsnForm;
